import { getApiBase } from '../utils/apiBase';
import { dedupedFetchJson, invalidateDedupCache } from '../utils/apiDedup';
// API Service Layer for Funstay Travel Platform
// Note: JWT-staleness (`TOKEN_STALE` 401) is handled globally by
// `utils/fetchInterceptor.js`, installed in `index.js` before React mounts.
// Every `fetch` call (including the ones below) flows through it.
const API_BASE_URL = getApiBase() || '';

function _normalizeAdminRuntimeContract(user = {}) {
  const primaryPosition = user.primary_position || null;
  const primaryPositionLabel = user.primary_position_label || primaryPosition?.label || '';
  const permissions = Array.isArray(user.permissions)
    ? user.permissions
    : Array.isArray(user.permission_matrix)
      ? user.permission_matrix
      : [];
  const moduleAccessKeys = Array.isArray(user.module_access_keys)
    ? user.module_access_keys
    : Array.isArray(primaryPosition?.module_access_keys)
      ? primaryPosition.module_access_keys
      : [];
  const accessModelVersion = user.access_model_version || '';
  const roleName = primaryPositionLabel || user.role_name || '';
  const isSuperAdmin = typeof user.is_super_admin === 'boolean'
    ? user.is_super_admin
    : false;

  return {
    primaryPosition,
    primaryPositionLabel,
    moduleAccessKeys,
    permissions,
    accessModelVersion,
    roleName,
    isSuperAdmin,
  };
}

function _buildStoredAdminUser(user = {}) {
  const runtime = _normalizeAdminRuntimeContract(user);
  return {
    ...user,
    primary_position: runtime.primaryPosition,
    primary_position_label: runtime.primaryPositionLabel,
    module_access_keys: runtime.moduleAccessKeys,
    permissions: runtime.permissions,
    access_model_version: runtime.accessModelVersion,
    is_super_admin: runtime.isSuperAdmin,
    role_name: runtime.roleName,
  };
}

function _persistAdminAuthResponse(response) {
  if (!response?.access_token) return response;
  localStorage.setItem('funstay_admin_token', response.access_token);
  const storedAdmin = _buildStoredAdminUser(response.user || {});
  localStorage.setItem('funstay_admin_user', JSON.stringify(storedAdmin));
  localStorage.setItem('funstay_admin_permissions', JSON.stringify(storedAdmin.permissions || []));
  localStorage.setItem('funstay_admin_is_super_admin', storedAdmin.is_super_admin ? 'true' : 'false');
  localStorage.setItem('funstay_admin_primary_position_label', storedAdmin.primary_position_label || '');
  return { ...response, user: storedAdmin };
}

// Auth-token helper used by both apiCall and the deduped public-GET helper.
function _authHeaders(endpoint) {
  const headers = { 'Content-Type': 'application/json' };
  const adminToken = localStorage.getItem('funstay_admin_token');
  const userToken = localStorage.getItem('funstay_token');
  const isAdminEndpoint = endpoint.includes('/admin') || endpoint.includes('/cms');
  const token = isAdminEndpoint ? (adminToken || userToken) : (userToken || adminToken);
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

/**
 * Lightweight wrapper around `dedupedFetchJson` for the small set of
 * page-global, read-only endpoints that multiple independent components
 * fetch on every page load (settings/agency-profile, cms/settings,
 * cms/testimonials, cms/pages/public/<slug>).
 *
 * Behaves like a non-throwing GET — returns `null` on a non-OK response,
 * matching the existing call-site error handling that uses
 * `.catch(() => null/[])`.
 */
async function dedupedGet(endpoint) {
  const url = `${API_BASE_URL}${endpoint}`;
  return dedupedFetchJson(url, { headers: _authHeaders(endpoint) });
}

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  // Add auth token if available - check admin token first for admin endpoints
  const adminToken = localStorage.getItem('funstay_admin_token');
  const userToken = localStorage.getItem('funstay_token');
  
  // Use admin token for admin/cms endpoints, otherwise use user token
  const isAdminEndpoint = endpoint.includes('/admin') || endpoint.includes('/cms');
  const token = isAdminEndpoint ? (adminToken || userToken) : (userToken || adminToken);
  
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'An error occurred' }));
      throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
}

// ============================================
// AUTHENTICATION
// ============================================

export const authAPI = {
  // Customer registration
  register: async (userData) => {
    const response = await apiCall('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    if (response.access_token) {
      localStorage.setItem('funstay_token', response.access_token);
      localStorage.setItem('funstay_user', JSON.stringify(response.user));
    }
    return response;
  },

  // Customer login
  login: async (email, password) => {
    const response = await apiCall('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (response.access_token) {
      localStorage.setItem('funstay_token', response.access_token);
      localStorage.setItem('funstay_user', JSON.stringify(response.user));
    }
    return response;
  },

  // Admin login
  adminLogin: async (email, password) => {
    const response = await apiCall('/api/auth/admin/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    return _persistAdminAuthResponse(response);
  },

  // Get current user profile
  getProfile: async () => {
    return await apiCall('/api/auth/me');
  },

  // Update profile
  updateProfile: async (data) => {
    return await apiCall('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Logout
  logout: () => {
    localStorage.removeItem('funstay_token');
    localStorage.removeItem('funstay_user');
  },

  // Admin logout
  adminLogout: () => {
    localStorage.removeItem('funstay_admin_token');
    localStorage.removeItem('funstay_admin_user');
    localStorage.removeItem('funstay_admin_permissions');
    localStorage.removeItem('funstay_admin_is_super_admin');
    localStorage.removeItem('funstay_admin_primary_position_label');
  },

  // Check if user is logged in
  isAuthenticated: () => {
    return !!localStorage.getItem('funstay_token');
  },

  // Check if admin is logged in
  isAdminAuthenticated: () => {
    return !!localStorage.getItem('funstay_admin_token');
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem('funstay_user');
    return user ? JSON.parse(user) : null;
  },

  // Get current admin user
  getCurrentAdmin: () => {
    const rawUser = localStorage.getItem('funstay_admin_user');
    const parsedUser = rawUser ? JSON.parse(rawUser) : null;
    if (!parsedUser) return null;
    const storedAdmin = _buildStoredAdminUser(parsedUser || {});
    localStorage.setItem('funstay_admin_user', JSON.stringify(storedAdmin));
    localStorage.setItem('funstay_admin_permissions', JSON.stringify(storedAdmin.permissions || []));
    localStorage.setItem('funstay_admin_is_super_admin', storedAdmin.is_super_admin ? 'true' : 'false');
    localStorage.setItem('funstay_admin_primary_position_label', storedAdmin.primary_position_label || '');
    return storedAdmin;
  },

  getAdminProfile: async () => {
    const response = await dedupedGet('/api/auth/me/profile');
    if (!response) {
      throw new Error('Unable to load admin profile');
    }
    const storedAdmin = _buildStoredAdminUser(response || {});
    localStorage.setItem('funstay_admin_user', JSON.stringify(storedAdmin));
    localStorage.setItem('funstay_admin_permissions', JSON.stringify(storedAdmin.permissions || []));
    localStorage.setItem('funstay_admin_is_super_admin', storedAdmin.is_super_admin ? 'true' : 'false');
    localStorage.setItem('funstay_admin_primary_position_label', storedAdmin.primary_position_label || '');
    return storedAdmin;
  },
};

// ============================================
// DESTINATIONS
// ============================================

export const destinationsAPI = {
  // Get all destinations
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/destinations/${query ? `?${query}` : ''}`);
  },

  // Get featured destinations
  getFeatured: async (limit = 6) => {
    return await apiCall(`/api/destinations/featured?limit=${limit}`);
  },

  // Get trending destinations
  getTrending: async (limit = 6) => {
    return await apiCall(`/api/destinations/trending?limit=${limit}`);
  },

  // Get destination by slug
  getBySlug: async (slug) => {
    return await apiCall(`/api/destinations/${slug}`);
  },

  // Get destination by ID
  getById: async (id) => {
    return await apiCall(`/api/destinations/id/${id}`);
  },

  // Admin: Get all destinations
  adminGetAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/destinations/admin/all${query ? `?${query}` : ''}`);
  },

  // Admin: Create destination
  create: async (data) => {
    return await apiCall('/api/destinations/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin: Update destination
  update: async (id, data) => {
    return await apiCall(`/api/destinations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin: Delete destination
  delete: async (id) => {
    return await apiCall(`/api/destinations/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin: linked packages for a destination
  getPackages: async (id) => {
    return await apiCall(`/api/destinations/${id}/packages`);
  },

  // Admin: live usage summary for a destination
  getUsage: async (id) => {
    return await apiCall(`/api/destinations/${id}/usage`);
  },
};

// ============================================
// LOOKUPS (lightweight typeahead selectors ONLY — see routes/lookups.py)
// ============================================
export const lookupsAPI = {
  destinations: async (q = '', limit = 30) =>
    await apiCall(`/api/lookups/destinations?q=${encodeURIComponent(q)}&limit=${limit}`),
  countries: async (q = '', limit = 40) =>
    await apiCall(`/api/lookups/countries?q=${encodeURIComponent(q)}&limit=${limit}`),
  states: async (country = '', q = '', limit = 60) =>
    await apiCall(`/api/lookups/states?country=${encodeURIComponent(country)}&q=${encodeURIComponent(q)}&limit=${limit}`),
  cities: async (country = '', state = '', q = '', limit = 30) =>
    await apiCall(`/api/lookups/cities?country=${encodeURIComponent(country)}&state=${encodeURIComponent(state)}&q=${encodeURIComponent(q)}&limit=${limit}`),
};

// ============================================
// PACKAGES
// ============================================

export const packagesAPI = {
  // Get all packages
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/packages/${query ? `?${query}` : ''}`);
  },

  // Get featured packages
  getFeatured: async (limit = 6) => {
    return await apiCall(`/api/packages/featured?limit=${limit}`);
  },

  // Get package by slug
  getBySlug: async (slug) => {
    return await apiCall(`/api/packages/${slug}`);
  },

  // Get package by ID
  getById: async (id) => {
    return await apiCall(`/api/packages/id/${id}`);
  },

  // Get group packages
  getGroupPackages: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/packages/group/all${query ? `?${query}` : ''}`);
  },

  // Get group package by slug
  getGroupBySlug: async (slug) => {
    return await apiCall(`/api/packages/group/${slug}`);
  },

  // Admin: Get all packages
  adminGetAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/packages/admin/all${query ? `?${query}` : ''}`);
  },

  // Admin: Create package
  create: async (data) => {
    return await apiCall('/api/packages/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin: Update package
  update: async (id, data) => {
    return await apiCall(`/api/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin: Delete package
  delete: async (id) => {
    return await apiCall(`/api/packages/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin: Get all group packages
  adminGetAllGroup: async () => {
    return await apiCall('/api/packages/group/admin/all');
  },

  // Admin: unified paginated search across regular + group packages
  adminSearch: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/packages/admin/search${query ? `?${query}` : ''}`);
  },

  // Admin: Create group package
  createGroup: async (data) => {
    return await apiCall('/api/packages/group', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin: Update group package
  updateGroup: async (id, data) => {
    return await apiCall(`/api/packages/group/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin: Delete group package
  deleteGroup: async (id) => {
    return await apiCall(`/api/packages/group/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin: Convert a package between regular ↔ group
  convertType: async (id, target_type) => {
    return await apiCall(`/api/packages/${id}/convert-type`, {
      method: 'POST',
      body: JSON.stringify({ target_type }),
    });
  },
};

// ============================================
// BLOGS
// ============================================

export const blogsAPI = {
  // Get all posts
  getAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/blogs/${query ? `?${query}` : ''}`);
  },

  // Get recent posts
  getRecent: async (limit = 5) => {
    return await apiCall(`/api/blogs/recent?limit=${limit}`);
  },

  // Get post by slug
  getBySlug: async (slug) => {
    return await apiCall(`/api/blogs/${slug}`);
  },

  // Get all categories
  getCategories: async () => {
    return await apiCall('/api/blogs/categories/all');
  },

  // Get all tags
  getTags: async () => {
    return await apiCall('/api/blogs/tags');
  },

  // Admin: Get all posts
  adminGetAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/blogs/admin/all${query ? `?${query}` : ''}`);
  },

  // Admin: Create post
  create: async (data) => {
    return await apiCall('/api/blogs/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin: Update post
  update: async (id, data) => {
    return await apiCall(`/api/blogs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin: Delete post
  delete: async (id) => {
    return await apiCall(`/api/blogs/${id}`, {
      method: 'DELETE',
    });
  },

  // Admin: Create category
  createCategory: async (data) => {
    return await apiCall('/api/blogs/categories', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// ============================================
// INQUIRIES
// ============================================

export const inquiriesAPI = {
  // Submit general inquiry
  submit: async (data) => {
    return await apiCall('/api/inquiries/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Submit contact form
  submitContact: async (data) => {
    return await apiCall('/api/inquiries/contact', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Submit package inquiry
  submitPackageInquiry: async (packageId, data) => {
    return await apiCall(`/api/inquiries/package/${packageId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Submit group trip inquiry
  submitGroupInquiry: async (packageId, data) => {
    return await apiCall(`/api/inquiries/group-trip/${packageId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Admin: Get all inquiries
  adminGetAll: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return await apiCall(`/api/inquiries/admin/all${query ? `?${query}` : ''}`);
  },

  // Admin: Get inquiry stats
  adminGetStats: async () => {
    return await apiCall('/api/inquiries/admin/stats');
  },

  // Admin: Update inquiry
  adminUpdate: async (id, data) => {
    return await apiCall(`/api/inquiries/admin/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  // Admin: Add note
  adminAddNote: async (id, note) => {
    return await apiCall(`/api/inquiries/admin/${id}/note?note=${encodeURIComponent(note)}`, {
      method: 'POST',
    });
  },
};

// ============================================
// CMS
// ============================================

export const cmsAPI = {
  // Get testimonials. Pass opts { destinationId, packageId, limit } for
  // context-aware fetching (linked first, then globals).
  // Deduped: multiple components asking for the same params on the same
  // page share one network round-trip via apiDedup (60s TTL).
  getTestimonials: async (limit = 10, opts = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (opts.destinationId) params.set('destination_id', opts.destinationId);
    if (opts.packageId) params.set('package_id', opts.packageId);
    return (await dedupedGet(`/api/cms/testimonials?${params.toString()}`)) || [];
  },

  // Get site settings (deduped — many components on every page need this).
  getSettings: async () => {
    return (await dedupedGet('/api/cms/settings')) || {};
  },

  // Admin: Get all testimonials
  adminGetTestimonials: async () => {
    return await apiCall('/api/cms/testimonials/admin/all');
  },

  // Admin: Create testimonial
  createTestimonial: async (data) => {
    const res = await apiCall('/api/cms/testimonials', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    invalidateDedupCache(`${API_BASE_URL}/api/cms/testimonials`);
    return res;
  },

  // Admin: Update testimonial
  updateTestimonial: async (id, data) => {
    const res = await apiCall(`/api/cms/testimonials/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateDedupCache(`${API_BASE_URL}/api/cms/testimonials`);
    return res;
  },

  // Admin: Delete testimonial
  deleteTestimonial: async (id) => {
    const res = await apiCall(`/api/cms/testimonials/${id}`, {
      method: 'DELETE',
    });
    invalidateDedupCache(`${API_BASE_URL}/api/cms/testimonials`);
    return res;
  },

  // Admin: Get pages
  getPages: async () => {
    return await apiCall('/api/cms/pages');
  },

  // Admin: Get page by ID
  getPage: async (id) => {
    return await apiCall(`/api/cms/pages/${id}`);
  },

  // Admin: Update page
  updatePage: async (id, data) => {
    const res = await apiCall(`/api/cms/pages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Public pages by slug are deduped — invalidate to ensure editors see
    // their saved changes immediately on the next public-page load.
    if (data && data.slug) {
      invalidateDedupCache(`${API_BASE_URL}/api/cms/pages/public/${data.slug}`);
    }
    return res;
  },

  // Public: Get page by slug (for public website). Deduped — every page
  // calls this once on mount, and several CMS-driven sections request it
  // again; this collapses them to a single round-trip per slug.
  getPublicPage: async (slug = 'home') => {
    return await dedupedGet(`/api/cms/pages/public/${slug}`);
  },

  // Public: Get all published pages
  getPublicPages: async () => {
    return await apiCall('/api/cms/pages/public');
  },

  // Get trending items (destinations + packages)
  getTrendingItems: async (limit = 8) => {
    return await apiCall(`/api/cms/trending-now?limit=${limit}`);
  },

  // Get spotlight blogs
  getSpotlightBlogs: async (limit = 3) => {
    return await apiCall(`/api/cms/spotlight-blogs?limit=${limit}`);
  },

  // Admin: Update footer settings
  updateFooter: async (data) => {
    const res = await apiCall('/api/cms/settings/footer', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateDedupCache(`${API_BASE_URL}/api/cms/settings`);
    return res;
  },

  // Admin: Update logo settings
  updateLogos: async (data) => {
    const res = await apiCall('/api/cms/settings/logos', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    invalidateDedupCache(`${API_BASE_URL}/api/cms/settings`);
    return res;
  },

  // Admin: Upload media
  uploadMedia: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = localStorage.getItem('funstay_admin_token');
    const response = await fetch(`${API_BASE_URL}/api/cms/media/upload`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail);
    }
    
    return await response.json();
  },
};

// Export all APIs
export default {
  auth: authAPI,
  destinations: destinationsAPI,
  lookups: lookupsAPI,
  packages: packagesAPI,
  blogs: blogsAPI,
  inquiries: inquiriesAPI,
  cms: cmsAPI,
};
